import { Boundaries } from "@planzone/core";
import { account, personalSynthesis } from "@planzone/dto";
import { events } from "@planzone/events";
import { setRequestStateToPending } from "../../utils/setRequestStateToPending";
import { setRequestStateToSuccess } from "../../utils/setRequestStateToSuccess";
import { setRequestStateToFailure } from "../../utils/setRequestStateToFailure";
import { createStateReducer } from "../../utils/createStateReducer";
import { resetState } from "../../utils/resetState";
import { State, Selector } from "./types";
import { createSelector } from "./selector";

const initialState: State = {
  request: {
    isPending: false,
    isSuccessful: false,
    isFailure: false,
  },
  account,
  synthesis: personalSynthesis,
};

function handleAuthentication(
  state: State,
  account: Boundaries["authenticate"]["success"],
): State {
  return { ...state, account };
}

function handleRequest(state: State): State {
  return {
    ...state,
    request: setRequestStateToPending(),
    synthesis: personalSynthesis,
  };
}

function handleSuccess(
  state: State,
  { synthesis }: Boundaries["retrievePersonalSynthesis"]["success"],
): State {
  return { ...state, request: setRequestStateToSuccess(), synthesis };
}

function handleFailure(state: State): State {
  return { ...state, request: setRequestStateToFailure() };
}

function handleRemoveTask(
  state: State,
  task: Boundaries["removeTask"]["success"],
): State {
  return {
    ...state,
    synthesis: {
      ...state.synthesis,
      overDueTasks: state.synthesis.overDueTasks.filter(
        ({ uuid }) => uuid !== task.uuid,
      ),
      upComingTasks: state.synthesis.upComingTasks.filter(
        ({ uuid }) => uuid !== task.uuid,
      ),
    },
  };
}

function handleEditTask(
  state: State,
  task: Boundaries["editTask"]["success"],
): State {
  return {
    ...state,
    synthesis: {
      ...state.synthesis,
      overDueTasks: state.synthesis.overDueTasks.map((t) =>
        t.uuid !== task.uuid
          ? t
          : {
              ...t,
              name: task.name,
              status: task.status,
              endDate: task.endDate,
            },
      ),
      upComingTasks: state.synthesis.upComingTasks.map((t) =>
        t.uuid !== task.uuid
          ? t
          : {
              ...t,
              name: task.name,
              status: task.status,
              endDate: task.endDate,
            },
      ),
    },
  };
}

const reduceState = resetState(
  initialState,
  createStateReducer(initialState, {
    [events.authenticateSucceeded]: handleAuthentication,
    [events.retrievePersonalSynthesisRequested]: handleRequest,
    [events.retrievePersonalSynthesisSucceeded]: handleSuccess,
    [events.retrievePersonalSynthesisFailed]: handleFailure,
    [events.editTaskSucceeded]: handleEditTask,
    [events.removeTaskSucceeded]: handleRemoveTask,
  }),
  [events.changePlanzoneSucceeded],
);

export type { State, Selector };

export { reduceState, initialState, createSelector };
